import { useEffect, useState, type FormEvent, type MouseEvent } from 'react';
import './ComingSoon.css';

type Lang = 'en' | 'no';

const STRINGS = {
  en: {
    headline: 'COMING SOON',
    sub: 'Quality focused, low intervention wine import from small producers with unique terroir.',
    season: 'Launching 2027',
    cta: 'Get in touch',
    ctaClose: 'Close',
    name: 'Your name',
    email: 'Your email',
    message: 'Anything you’d like to share — questions?',
    send: 'Send',
    sending: 'Sending…',
    error: 'That didn’t go through — try again, or write us directly.',
    thanks: 'Thank you. We’ll be in touch soon.',
    meta: 'Wine import — Oslo, Norway',
    privacy: 'Privacy',
  },
  no: {
    headline: 'KOMMER SNART',
    sub: 'Kvalitetsfokusert import av vin fra små produsenter med unik terroir.',
    season: 'Lanseres 2027',
    cta: 'Ta kontakt',
    ctaClose: 'Lukk',
    name: 'Navn',
    email: 'E-post',
    message: 'Noe du har på hjertet — spørsmål?',
    send: 'Send',
    sending: 'Sender…',
    error: 'Det gikk ikke gjennom — prøv igjen, eller skriv direkte.',
    thanks: 'Takk. Vi tar kontakt snart.',
    meta: 'Vinimport — Oslo, Norge',
    privacy: 'Personvern',
  },
} satisfies Record<Lang, Record<string, string>>;

const CONTACT_EMAILS: Record<Lang, string> = {
  en: 'hello@solrick.com',
  no: 'hei@solrick.com',
};

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem('solrick-lang');
    return v === 'no' ? 'no' : 'en';
  } catch {
    return 'en';
  }
}

function trackCursor(e: MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty('--mx', `${e.clientX - r.left}px`);
  el.style.setProperty('--my', `${e.clientY - r.top}px`);
}

export default function ComingSoon() {
  const [lang, setLang] = useState<Lang>(readStoredLang);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [gotcha, setGotcha] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle');
  const [submitted, setSubmitted] = useState(false);

  const t = STRINGS[lang];
  const contactEmail = CONTACT_EMAILS[lang];

  useEffect(() => {
    try { localStorage.setItem('solrick-lang', lang); } catch {}
    document.documentElement.lang = lang === 'no' ? 'nb' : 'en';
  }, [lang]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          lang,
          _gotcha: gotcha,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setSubmitted(true);
      setStatus('idle');
    } catch {
      setStatus('error');
    }
  };

  return (
    <main className="cs-stage">
      <header className="cs-top">
        <a className="cs-corner-mark" href="/" aria-label="Solrick">
          <img src="/logo-text-only.svg" alt="Solrick" />
        </a>
        <div className="cs-lang" role="group" aria-label="Language">
          <button
            type="button"
            className={'cs-lang-btn' + (lang === 'en' ? ' is-on' : '')}
            onClick={() => setLang('en')}
            aria-pressed={lang === 'en'}
          >
            EN
          </button>
          <span className="cs-lang-sep" aria-hidden>/</span>
          <button
            type="button"
            className={'cs-lang-btn' + (lang === 'no' ? ' is-on' : '')}
            onClick={() => setLang('no')}
            aria-pressed={lang === 'no'}
          >
            NO
          </button>
        </div>
      </header>

      <section className={'cs-center' + (open ? ' is-open' : '')}>
        <div
          className={'cs-logo' + (open ? ' cs-logo-text' : '')}
          role="img"
          aria-label="Solrick"
        >
          <img
            className="cs-logo-img-full"
            src="/logo-main.svg"
            alt=""
            aria-hidden
          />
          <img
            className="cs-logo-img-text"
            src="/logo-with-rays.svg"
            alt=""
            aria-hidden
          />
        </div>

        <h1 className="cs-headline">{t.headline}</h1>

        <p className="cs-sub">{t.sub}</p>

        <div className="cs-soon" onMouseMove={trackCursor}>
          <span className="cs-wine" aria-hidden>
            <span className="cs-wine-glass" />
            {(['low', 'med', 'high', 'full'] as const).map((level, i) => (
              <span
                key={level}
                className="cs-wine-frame"
                style={{
                  ['--i' as string]: i,
                  maskImage: `url(/wine-fill-${level}.svg)`,
                  WebkitMaskImage: `url(/wine-fill-${level}.svg)`,
                }}
              />
            ))}
          </span>
          <span className="t-eyebrow">{t.season}</span>
        </div>

        {!submitted ? (
          <div className={'cs-contact' + (open ? ' is-open' : '')}>
            <button
              type="button"
              className="cs-cta"
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              aria-controls="cs-contact-form"
            >
              <span>{open ? t.ctaClose : t.cta}</span>
              <span className="cs-cta-chev" aria-hidden>{open ? '×' : '→'}</span>
            </button>

            <div
              id="cs-contact-form"
              className="cs-contact-panel"
              aria-hidden={!open}
            >
              <form className="cs-form-grid" onSubmit={onSubmit} noValidate>
                <label className="cs-field">
                  <span className="cs-field-label">{t.name}</span>
                  <input
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                </label>
                <label className="cs-field">
                  <span className="cs-field-label">{t.email}</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </label>
                <label className="cs-field cs-field-wide">
                  <span className="cs-field-label">{t.message}</span>
                  <textarea
                    rows={3}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                  />
                </label>
                {/* Honeypot — hidden visually, bots fill it, humans don't. */}
                <input
                  type="text"
                  name="_gotcha"
                  className="cs-honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden
                  value={gotcha}
                  onChange={e => setGotcha(e.target.value)}
                />
                {status === 'error' && (
                  <p className="cs-form-error" role="alert">{t.error}</p>
                )}
                <div className="cs-form-actions">
                  <a className="cs-email-direct" href={`mailto:${contactEmail}`}>
                    <span>{contactEmail}</span>
                    <span className="arrow" aria-hidden>→</span>
                  </a>
                  <button
                    type="submit"
                    className="cs-send"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? t.sending : t.send}
                    <span className="arrow" aria-hidden>→</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="cs-thanks">
            <span className="t-italic">{t.thanks}</span>
          </div>
        )}
      </section>

      <footer className="cs-bottom">
        <span className="cs-bottom-meta">{t.meta}</span>
        <a className="cs-privacy" href="/privacy">{t.privacy}</a>
        <a
          className="cs-ig"
          href="https://instagram.com/solrickwines"
          target="_blank"
          rel="noopener noreferrer"
        >
          @solrickwines
        </a>
      </footer>
    </main>
  );
}
